// components/LibraryBrowser.tsx
import { FC, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import axios from 'axios';
import { FavoriteAlbum } from '../types/favorites';
import { Tag } from '../types/tags';
import { useSpotifyPlayback } from '../contexts/SpotifyPlaybackContext';
import { compareAlphabetically } from '../utils/sorting';
import PlaybackStatusMessage from "./PlaybackStatusMessage";
import ArtistTagSelector from './ArtistTagSelector';

interface LibraryBrowserProps {
  accessToken: string;
}

const LibraryBrowser: FC<LibraryBrowserProps> = ({ accessToken }) => {
  const router = useRouter();
  const { playAlbum } = useSpotifyPlayback();
  const [albums, setAlbums] = useState<FavoriteAlbum[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [artistFilter, setArtistFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('artist');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [playbackStatuses, setPlaybackStatuses] = useState<{[key: string]: string | null}>({});
  const [editingArtist, setEditingArtist] = useState<string | null>(null);
  const [groupByArtist, setGroupByArtist] = useState(true);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        const response = await axios.get<Tag[]>('/api/tags');
        setTags(response.data);
      } catch (error) {
        console.error('Error fetching tags:', error);
      }
    };

    fetchTags();
  }, []);

  const fetchAlbums = async () => {
    if (!selectedTag && !artistFilter.trim()) return;
    setLoading(true);
    try {
      const params: any = {
        sortBy,
        order: sortOrder,
        access_token: accessToken,
      };

      if (selectedTag) {
        params.tag = selectedTag;
      }

      if (artistFilter) {
        params.artist = artistFilter;
      }

      const response = await axios.get<FavoriteAlbum[]>('/api/albums/library', { params });

      // Apply client-side sorting for proper alphabetical handling of articles
      const sortedAlbums = [...response.data].sort((a, b) => {
        let comparison = 0;

        switch (sortBy) {
          case 'name':
            comparison = compareAlphabetically(a.name, b.name);
            break;
          case 'artist':
            comparison = compareAlphabetically(a.artist, b.artist);
            break;
          case 'rating':
            comparison = (a.rating || 0) - (b.rating || 0);
            break;
          case 'last_played':
            const aDate = a.last_played ? new Date(a.last_played).getTime() : 0;
            const bDate = b.last_played ? new Date(b.last_played).getTime() : 0;
            comparison = aDate - bDate;
            break;
          case 'play_count':
            comparison = (a.play_count || 0) - (b.play_count || 0);
            break;
          case 'year':
            comparison = Number(a.year || 0) - Number(b.year || 0);
            break;
          case 'tag':
            comparison = compareAlphabetically(a.tags?.[0]?.name || '', b.tags?.[0]?.name || '');
            break;
          case 'popularity':
            comparison = (a.popularity || 0) - (b.popularity || 0);
            break;
          default:
            comparison = compareAlphabetically(a.name, b.name);
        }

        return sortOrder === 'asc' ? comparison : -comparison;
      });

      setAlbums(sortedAlbums);
      setHasSearched(true);
    } catch (error) {
      console.error('Error fetching albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if clicking the same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to ascending when selecting a new column
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const clearFilters = () => {
    setSelectedTag('');
    setArtistFilter('');
    setSortBy('artist');
    setSortOrder('asc');
    setAlbums([]);
    setHasSearched(false);
  };

  const handlePlayAlbum = async (album: any) => {
    // Clear any previous status for this album
    setPlaybackStatuses(prev => ({
      ...prev,
      [album.id]: null
    }));

    // Check if we have a Spotify album ID
    if (!album.spotify_album_id) {
      console.error('Missing spotify_album_id for album:', album);
      setPlaybackStatuses(prev => ({
        ...prev,
        [album.id]: "Error: Missing Spotify album ID"
      }));
      return;
    }

    if (accessToken) {
      const result = await playAlbum(album.spotify_album_id);

      if (result.success) {
        setPlaybackStatuses(prev => ({
          ...prev,
          [album.id]: "Now playing"
        }));
      } else {
        setPlaybackStatuses(prev => ({
          ...prev,
          [album.id]: `Error: ${result.message}`
        }));
      }
    }
  };

  const albumsByArtist = Object.entries(
    albums.reduce<Record<string, FavoriteAlbum[]>>((groups, album) => {
      (groups[album.artist] ||= []).push(album);
      return groups;
    }, {})
  ).sort(([artistA], [artistB]) => compareAlphabetically(artistA, artistB));
  const displayedGroups: Array<[string, FavoriteAlbum[]]> = groupByArtist ? albumsByArtist : [['All Albums', albums]];
  const canSearch = Boolean(selectedTag || artistFilter.trim());

  return (
      <div className="compact-view">
        {/* Filters */}
        <div className="compact-panel mb-3">
          <h2 className="text-lg font-bold mb-3">Filter Albums</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-sm font-medium mb-1">By Tag</label>
              <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="w-full border rounded p-2"
              >
                <option value="">All Tags</option>
                {tags.map(tag => (
                    <option key={tag.id} value={tag.name}>{tag.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">By Artist</label>
              <input
                  type="text"
                  value={artistFilter}
                  onChange={(e) => setArtistFilter(e.target.value)}
                  placeholder="Filter by artist name"
                  className="w-full border rounded p-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Sort By</label>
              <div className="flex">
                <select
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    className="flex-grow border rounded p-2"
                >
                  <option value="name">Album Title</option>
                  <option value="artist">Artist</option>
                  <option value="rating">My Rating</option>
                  <option value="last_played">Last Played</option>
                  <option value="play_count">Play Count</option>
                  <option value="year">Year</option>
                  <option value="tag">Tag</option>
                  <option value="popularity">Popularity</option>
                </select>

                <button
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    className="ml-2 border rounded p-2"
                    title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button onClick={fetchAlbums} disabled={!canSearch || loading} className="bg-green-500 hover:bg-green-600 disabled:bg-gray-600 disabled:text-gray-400 px-4 rounded font-semibold">Search</button>
            <button onClick={clearFilters} className="bg-gray-200 hover:bg-gray-300 px-4 rounded">Clear</button>
            <label className="ml-auto flex items-center gap-2 text-sm"><input type="checkbox" checked={groupByArtist} onChange={event => setGroupByArtist(event.target.checked)} /> Group by Artist</label>
          </div>
        </div>

        {/* Albums grouped by artist */}
        {!hasSearched && !loading ? (
            <div className="compact-panel text-center text-gray-500">Choose an artist or tag, then tap Search.</div>
        ) : loading ? (
            <div className="text-center py-8">Loading your library...</div>
        ) : albums.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-lg mb-4">No albums found in your library.</p>
              <p>Try adjusting your filters or add some albums from the search page.</p>
              <button
                  onClick={() => router.push('/explore?section=find')}
                  className="mt-4 bg-green-500 hover:bg-green-600 text-white px-5 py-2 rounded-full"
              >
                Search for Albums
              </button>
            </div>
        ) : (
            <div className="space-y-4">
              {displayedGroups.map(([artist, artistAlbums]) => (
                <section key={artist} className="compact-panel p-0 overflow-hidden" aria-labelledby={`artist-${artist.replace(/[^a-z0-9]/gi, '-')}`}>
                  <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-100 border-b">
                    <div className="min-w-0">
                      <h2 id={`artist-${artist.replace(/[^a-z0-9]/gi, '-')}`} className="font-bold truncate">{artist}</h2>
                      <span className="text-sm text-gray-500">{artistAlbums.length} album{artistAlbums.length === 1 ? '' : 's'}</span>
                    </div>
                    {groupByArtist && <button onClick={() => setEditingArtist(editingArtist === artist ? null : artist)} className="compact-button border bg-white rounded px-3 text-sm">
                      {editingArtist === artist ? 'Done' : 'Edit tags'}
                    </button>}
                  </div>
                  {groupByArtist && editingArtist === artist && (
                    <div className="border-b p-3 bg-blue-50">
                      <ArtistTagSelector
                        artistId={artistAlbums[0].artist_id}
                        albumId={artistAlbums[0].id}
                        accessToken={accessToken}
                        initialTags={artistAlbums[0].tags || []}
                        onArtistResolved={(artistId) => setAlbums(current => current.map(album =>
                          album.artist === artist ? { ...album, artist_id: artistId } : album
                        ))}
                        onTagsChange={(nextTags) => setAlbums(current => current.map(album =>
                          album.artist === artist ? { ...album, tags: nextTags, artist_id: album.artist_id || artistAlbums[0].artist_id } : album
                        ))}
                      />
                    </div>
                  )}
                  <div className="divide-y">
                    {artistAlbums.map(album => (
                    <article key={album.id} className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_5rem_7rem_10rem] gap-2 items-center px-3 py-2 bg-white">
                      <div className="min-w-0">
                        <h3 className="font-semibold truncate">{album.name}</h3>
                        {!groupByArtist && <p className="text-sm text-gray-500 truncate">{album.artist}</p>}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {album.tags?.map(tag => <span key={tag.id} className="text-xs bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded">{tag.name}</span>)}
                        </div>
                      </div>
                      <p className="hidden sm:block text-sm text-gray-500">{album.year || '—'}</p>

                      <div className="hidden sm:block text-sm">
                        <p className="font-semibold">{album.play_count || 0} listens</p>
                        <p className="text-xs text-gray-500">{album.rating ? `${album.rating}★` : 'Unrated'}</p>
                      </div>
                      <div className="flex gap-1 justify-end">
                        <button
                            onClick={() => router.push(`/albums/${album.id}`)}
                            className="compact-button bg-blue-500 hover:bg-blue-600 text-white px-3 rounded text-sm"
                        >
                          Details
                        </button>

                        <button
                            onClick={() => handlePlayAlbum(album)}
                            className="compact-button bg-green-500 hover:bg-green-600 text-white px-3 rounded text-sm"
                        >
                          Play
                        </button>
                        <PlaybackStatusMessage status={playbackStatuses[album.id]} />
                      </div>
                      <p className="col-span-2 sm:col-span-4 text-xs text-gray-500 sm:-mt-1">
                        Last played {album.last_played ? new Date(album.last_played).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : 'never'}
                      </p>
                    </article>
                    ))}
                  </div>
                </section>
              ))}
            </div>
        )}
      </div>
  );
};

export default LibraryBrowser;
